import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaSearch,
  FaFilter,
  FaStar,
  FaGhost,
  FaTimes,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { searchAPI } from '../services/api';
import { useDarkMode } from '../context/DarkModeContext';
import toast from 'react-hot-toast';

const AdvancedSearch = ({ initialQuery = '', onClose = null }) => {
  const { darkMode } = useDarkMode();
  // const navigate = useNavigate(); // Not used currently

  // Search filters
  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [model, setModel] = useState('');
  const [minRating, setMinRating] = useState('');
  const [maxRating, setMaxRating] = useState('');
  const [minTokens, setMinTokens] = useState('');
  const [maxTokens, setMaxTokens] = useState('');
  const [username, setUsername] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Results
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Categories
  const [categories, setCategories] = useState([]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/categories`);
        const data = await response.json();
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Perform search
  const performSearch = async (offset = 0) => {
    try {
      setLoading(true);

      const params = {
        q: query,
        category: category || undefined,
        tags: tags || undefined,
        model: model || undefined,
        minRating: minRating || undefined,
        maxRating: maxRating || undefined,
        minTokens: minTokens || undefined,
        maxTokens: maxTokens || undefined,
        username: username || undefined,
        sortBy,
        sortOrder,
        limit: 20,
        offset
      };

      const response = await searchAPI.prompts(params);

      if (response?.data) {
        if (offset === 0) {
          setResults(response.data.prompts || []);
        } else {
          setResults(prev => [...prev, ...(response.data.prompts || [])]);
        }
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(0);
  };

  // Clear filters
  const clearFilters = () => {
    setQuery('');
    setCategory('');
    setTags('');
    setModel('');
    setMinRating('');
    setMaxRating('');
    setMinTokens('');
    setMaxTokens('');
    setUsername('');
    setSortBy('created_at');
    setSortOrder('DESC');
    setResults([]);
    setPagination(null);
  };

  // Load more results
  const loadMore = () => {
    if (pagination?.hasMore) {
      performSearch(pagination.offset + 20);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-dark-bg text-ghost-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FaSearch className="text-purple-500" />
            Advanced Search
          </h1>
          {onClose && (
            <button
              onClick={onClose}
              className={`p-2 rounded-lg ${
                darkMode ? 'hover:bg-dark-hover' : 'hover:bg-gray-200'
              }`}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-6">
          {/* Main search bar */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <FaSearch className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search prompts, users, tags..."
                className={`w-full pl-12 pr-4 py-3 rounded-lg border ${
                  darkMode
                    ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Toggle filters button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              darkMode
                ? 'bg-dark-surface border-dark-border hover:bg-dark-hover'
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FaFilter />
            <span>Filters</span>
            {showFilters ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          {/* Advanced Filters */}
          {showFilters && (
            <div className={`mt-4 p-6 rounded-lg border ${
              darkMode ? 'bg-dark-surface border-dark-border' : 'bg-white border-gray-300'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* AI Model */}
                <div>
                  <label className="block text-sm font-medium mb-2">AI Model</label>
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g., GPT-4, Claude"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium mb-2">Author</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Min Rating */}
                <div>
                  <label className="block text-sm font-medium mb-2">Min Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                    placeholder="0-10"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Max Rating */}
                <div>
                  <label className="block text-sm font-medium mb-2">Max Rating</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={maxRating}
                    onChange={(e) => setMaxRating(e.target.value)}
                    placeholder="0-10"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Min Tokens */}
                <div>
                  <label className="block text-sm font-medium mb-2">Min Tokens</label>
                  <input
                    type="number"
                    min="0"
                    value={minTokens}
                    onChange={(e) => setMinTokens(e.target.value)}
                    placeholder="e.g., 100"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="block text-sm font-medium mb-2">Max Tokens</label>
                  <input
                    type="number"
                    min="0"
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(e.target.value)}
                    placeholder="e.g., 1000"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="productivity, coding"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white placeholder-gray-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-medium mb-2">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="created_at">Date Created</option>
                    <option value="updated_at">Date Updated</option>
                    <option value="rating">Rating</option>
                    <option value="views">Views</option>
                    <option value="ratings_count">Number of Ratings</option>
                    <option value="forks">Forks</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium mb-2">Order</label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border ${
                      darkMode
                        ? 'bg-dark-surface border-dark-border text-ghost-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="DESC">Descending</option>
                    <option value="ASC">Ascending</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`px-4 py-2 rounded-lg border ${
                    darkMode
                      ? 'border-dark-border hover:bg-dark-hover'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </form>

        {/* Results */}
        {pagination && (
          <div className="mb-4 text-sm">
            <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
              {pagination.total} results found • Page {pagination.currentPage} of {pagination.pages}
            </span>
          </div>
        )}

        {results.length > 0 ? (
          <div className="space-y-4">
            {results.map((prompt) => (
              <Link
                key={prompt.id}
                to={`/prompt/${prompt.id}`}
                className={`block p-6 rounded-lg border transition-all ${
                  darkMode
                    ? 'bg-dark-surface border-dark-border hover:border-purple-500'
                    : 'bg-white border-gray-200 hover:border-purple-500'
                } hover:shadow-lg`}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold">{prompt.title}</h3>
                  {prompt.average_rating > 0 && (
                    <div className="flex items-center gap-1 text-yellow-500">
                      <FaStar />
                      <span className="font-medium">{(parseFloat(prompt.average_rating) || 0).toFixed(1)}</span>
                      <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        ({prompt.total_ratings})
                      </span>
                    </div>
                  )}
                </div>

                <p className={`mb-4 line-clamp-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {prompt.description}
                </p>

                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                    by {prompt.username}
                  </span>

                  {prompt.category_name && (
                    <span className="px-3 py-1 bg-purple-500 text-white rounded-full text-xs">
                      {prompt.category_name}
                    </span>
                  )}

                  {prompt.target_ai_model && (
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      darkMode ? 'bg-dark-hover' : 'bg-gray-100'
                    }`}>
                      {prompt.target_ai_model}
                    </span>
                  )}

                  {prompt.token_count && (
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                      {prompt.token_count} tokens
                    </span>
                  )}

                  <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                    {prompt.views_count} views
                  </span>
                </div>

                {prompt.tags && prompt.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {prompt.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className={`text-xs px-2 py-1 rounded ${
                          darkMode ? 'bg-dark-hover text-gray-400' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            ))}

            {/* Load More */}
            {pagination?.hasMore && (
              <button
                onClick={loadMore}
                disabled={loading}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  darkMode
                    ? 'bg-dark-surface text-ghost-white border border-dark-border hover:bg-dark-hover'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        ) : !loading && query ? (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <FaGhost className="text-6xl mx-auto mb-4 opacity-50" />
            <p className="text-lg">No results found for "{query}"</p>
            <p className="text-sm mt-2">Try adjusting your search criteria</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdvancedSearch;
